import { useState, useEffect, useCallback } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import "@/App.css";
import LandingPage from "./components/LandingPage";
import AuthView from "./components/AuthView";
import VerificationView from "./components/VerificationView";
import ProfileSetup from "./components/ProfileSetup";
import DiscoveryView from "./components/DiscoveryView";
import MatchesView from "./components/MatchesView";
import ChatView from "./components/ChatView";
import AdminPanel from "./components/AdminPanel";
import ProfileView from "./components/ProfileView";
import Sidebar from "./components/Sidebar";
import { Toaster } from "./components/ui/sonner";
import PlaceholderPage from "./components/public/PlaceholderPage";
import ContactPage from "./components/public/ContactPage";
import { publicPages } from "./routes/publicPages";
import ScrollToTop from "./routes/ScrollToTop";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function AppShell() {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [showLanding, setShowLanding] = useState(true);
  const [authMode, setAuthMode] = useState("login"); // "login" or "register"
  const [activeView, setActiveView] = useState("discovery");
  const [chatMatch, setChatMatch] = useState(null);
  const [chatUser, setChatUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const savedToken = localStorage.getItem("token");
    if (savedToken) {
      setShowLanding(false);
      fetchUser(savedToken);
    } else {
      setLoading(false);
    }
    // Seed demo data
    fetch(`${API}/seed`, { method: "POST" }).catch(() => {});
  }, []);

  const fetchUser = async (t, retries = 2) => {
    try {
      const res = await fetch(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${t}` },
      });
      if (res.ok) {
        const data = await res.json();
        setUser(data.user);
        setToken(t);
      } else if (res.status === 401) {
        // Only clear token on explicit 401 (expired/invalid)
        localStorage.removeItem("token");
      }
      // Don't clear token on other errors (500, etc.)
    } catch (err) {
      console.error("Auth check failed:", err);
      // Network error - retry instead of logging out
      if (retries > 0) {
        await new Promise((r) => setTimeout(r, 1000));
        return fetchUser(t, retries - 1);
      }
      // After retries, keep token but show app (user stays logged in on next reload)
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = useCallback((userData, t) => {
    setUser(userData);
    setToken(t);
    setShowLanding(false);
  }, []);

  const handleLogout = useCallback(() => {
    localStorage.removeItem("token");
    setUser(null);
    setToken(null);
    setActiveView("discovery");
    setChatMatch(null);
    setChatUser(null);
    setShowLanding(true);
  }, []);

  const handleVerificationChange = useCallback(
    (status) => {
      setUser((prev) => ({ ...prev, verification_status: status }));
    },
    []
  );

  const handleProfileComplete = useCallback((updatedUser) => {
    setUser(updatedUser);
  }, []);

  const handleOpenChat = useCallback((matchId, matchUserData) => {
    setChatMatch(matchId);
    setChatUser(matchUserData);
    setActiveView("chat");
  }, []);

  const handleChatBack = useCallback(() => {
    setChatMatch(null);
    setChatUser(null);
    setActiveView("matches");
  }, []);

  // Loading
  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center bg-[#050505]">
        <p
          style={{ fontFamily: "'JetBrains Mono', monospace" }}
          className="text-[#00FF66] text-sm tracking-widest uppercase animate-pulse"
          data-testid="loading-indicator"
        >
          Initializing secure connection...
        </p>
      </div>
    );
  }

  // Not authenticated
  if (!user) {
    // Show landing page first
    if (showLanding) {
      return (
        <>
          <LandingPage
            onGetStarted={() => {
              setAuthMode("register");
              setShowLanding(false);
            }}
            onSignIn={() => {
              setAuthMode("login");
              setShowLanding(false);
            }}
          />
          <Toaster />
        </>
      );
    }
    // Show auth screen
    return (
      <>
        <AuthView
          onLogin={handleLogin}
          defaultMode={authMode}
          onBackToLanding={() => setShowLanding(true)}
        />
        <Toaster />
      </>
    );
  }

  // Needs verification
  if (
    user.verification_status !== "verified" &&
    !user.is_admin
  ) {
    return (
      <>
        <div className="h-screen overflow-y-auto bg-[#050505]">
          <div className="flex items-center justify-between px-6 py-4 border-b border-[#1F2027]">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-[#FF2E2E]" />
              <span
                style={{ fontFamily: "'JetBrains Mono', monospace" }}
                className="text-[#8A8A93] text-xs tracking-widest uppercase"
              >
                Clearance Required
              </span>
            </div>
            <button
              data-testid="verification-logout-btn"
              onClick={handleLogout}
              className="text-[#8A8A93] text-sm hover:text-[#FAFAFA] transition-colors"
            >
              Sign Out
            </button>
          </div>
          <VerificationView
            user={user}
            token={token}
            onStatusChange={handleVerificationChange}
          />
        </div>
        <Toaster />
      </>
    );
  }

  // Needs profile completion
  if (!user.profile_complete && !user.is_admin) {
    return (
      <>
        <ProfileSetup
          user={user}
          token={token}
          onComplete={handleProfileComplete}
        />
        <Toaster />
      </>
    );
  }

  // Main app
  const renderView = () => {
    switch (activeView) {
      case "discovery":
        return <DiscoveryView token={token} onMatch={() => {}} />;
      case "matches":
        return <MatchesView token={token} onOpenChat={handleOpenChat} />;
      case "chat":
        if (chatMatch && chatUser) {
          return (
            <ChatView
              token={token}
              matchId={chatMatch}
              matchUser={chatUser}
              currentUser={user}
              onBack={handleChatBack}
            />
          );
        }
        return <MatchesView token={token} onOpenChat={handleOpenChat} />;
      case "profile":
        return (
          <ProfileView
            user={user}
            token={token}
            onUpdate={(u) => setUser(u)}
          />
        );
      case "admin":
        return user.is_admin ? (
          <AdminPanel token={token} />
        ) : (
          <DiscoveryView token={token} onMatch={() => {}} />
        );
      default:
        return <DiscoveryView token={token} onMatch={() => {}} />;
    }
  };

  return (
    <div className="app-container grain-overlay">
      <Sidebar
        activeView={activeView}
        onViewChange={setActiveView}
        isAdmin={user?.is_admin}
        onLogout={handleLogout}
      />
      <div className="main-content">{renderView()}</div>
      <Toaster />
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <ScrollToTop />
      <Routes>
        <Route path="/" element={<AppShell />} />
        <Route path="/contact" element={<ContactPage />} />
        {publicPages.map((p) => (
          <Route
            key={p.path}
            path={p.path}
            element={
              <PlaceholderPage
                eyebrow={p.eyebrow}
                title={p.title}
                description={p.description}
                sections={p.sections}
                press={p.press}
                stories={p.stories}
                storyGroups={p.storyGroups}
                testId={`page-${p.path.replace("/", "")}`}
              />
            }
          />
        ))}
        <Route path="*" element={<AppShell />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
